import type { DurableChatTurnMode, Feature } from "@/api/models"

import { ApiError } from "@/api/client/_shared"
import { DURABLE_CHAT_TURN_MODES } from "@/api/models"

const DURABLE_CHAT_TURNS_FEATURE_ID = "durable_chat_turns"
const durableModes = new Set<string>(DURABLE_CHAT_TURN_MODES)

export type DurableChatTurnModeSelection = {
  isDeepExplorer?: boolean
  isTranslator?: boolean
  startResearch?: boolean
  enableAgent?: boolean
}

export function getDurableChatTurnMode({
  isDeepExplorer = false,
  isTranslator = false,
  startResearch = false,
  enableAgent = false,
}: DurableChatTurnModeSelection): DurableChatTurnMode | null {
  if (isDeepExplorer) return null
  if (isTranslator) return "translate"
  if (startResearch) return "scholar"
  if (enableAgent) return "agent"
  return "explorer"
}

export type DurableExplorerSendEligibility = {
  isContinuation: boolean
  /**
   * Whether this send carries freshly-attached, not-yet-indexed file bytes.
   * The durable HTTP accept payload has NO channel for base64 upload bytes (the
   * legacy WS reconstructs them from `data.files`), so a durable send would
   * silently drop them. Upload-bearing turns must stay on the legacy transport.
   */
  hasFreshUploads: boolean
  durableMode: DurableChatTurnMode | null | undefined
  isExplorerModeEnabled: boolean
  hasDurableRequestBuilder: boolean
}

/**
 * Whether a send should take the durable-turn path (Explorer only). This is the
 * single structural gate mirrored by `use-chat.ts`'s `sendChatRequest`:
 * continuations are excluded (the durable snapshot contract has no `session_state`
 * continuation equivalent), fresh uploads are excluded (see `hasFreshUploads`),
 * and the caller must have wired both the mode resolver and the request builder.
 */
export function isDurableExplorerSendEligible({
  isContinuation,
  hasFreshUploads,
  durableMode,
  isExplorerModeEnabled,
  hasDurableRequestBuilder,
}: DurableExplorerSendEligibility): boolean {
  return (
    !isContinuation &&
    !hasFreshUploads &&
    durableMode === "explorer" &&
    isExplorerModeEnabled &&
    hasDurableRequestBuilder
  )
}

// The SPA catch-all's fixed 404 body for an unrecognized API path (a backend
// that predates the durable-turns routes entirely). Verified verbatim
// against app/backend/routes/static.py's `spa_catch_all`: `{"error": f"Unknown
// API route: /{path}"}`. Deliberately NOT matched against the durable
// routes' own legitimate `{"error": "Session not found"}` 404 (returned for
// a non-participant session, see app/backend/routes/chat_turns.py) — that
// 404 must never fall back, and its `error` text never starts with this
// prefix, so a prefix match distinguishes the two without ambiguity.
const UNKNOWN_API_ROUTE_ERROR_PREFIX = "Unknown API route:"

/**
 * Whether a 404 is the SPA catch-all's unknown-route shape rather than a
 * durable route's own legitimate "Session not found" 404 (see
 * `UNKNOWN_API_ROUTE_ERROR_PREFIX`'s doc comment above for the exact
 * distinction). Shared by `shouldFallbackToLegacyTransport` below and the
 * discovery poll's own failure classification
 * (`isPermanentPollFailure` in use-durable-chat-turn-hydration.ts) — both
 * need the identical disambiguation, and duplicating it risked the two
 * silently drifting (issue #2513: the poll's first version conflated EVERY
 * 404 as permanent, which would misclassify this exact catch-all case during
 * a routine deploy window as "this session will never have durable turns,"
 * reintroducing the bug that fix was written to close).
 */
export function isUnknownApiRouteError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.bodyError?.startsWith(UNKNOWN_API_ROUTE_ERROR_PREFIX) === true
  )
}

/**
 * Whether a durable-turn HTTP acceptance failure means "durable transport is
 * not available here" — the narrow, deliberately conservative set of signals
 * under which a turn must be retried on the legacy transport instead of
 * surfacing as a hard error (issue #2513). Only:
 *   - 403 with body `code: "durable_turns_disabled"` (backend has the
 *     durable-turns feature/mode gate closed — the concrete bug this exists
 *     for: frontend gate open, backend gate closed).
 *   - 404 whose body is the SPA catch-all's unknown-route shape (kept for
 *     defense in depth — see the 405 note below for why the accept call
 *     itself won't actually produce this).
 *   - 405 on the accept call (a backend predating the durable-turns routes
 *     entirely). The accept call is a POST; the SPA catch-all
 *     (`spa_catch_all` in app/backend/routes/static.py) only registers
 *     GET/HEAD/OPTIONS, so Werkzeug/Quart matches the path but rejects the
 *     method with 405 before the catch-all's own 404 JSON body is ever
 *     produced — the 404 arm above is therefore unreachable for this call in
 *     practice. 405 is safe to match unconditionally here (no body-shape
 *     check needed): chat_turns.py registers POST on this exact `/turns`
 *     path, so a 405 response to a POST there can only mean the route isn't
 *     registered at all, never a legitimate durable-turn response.
 *
 * Deliberately excludes 409 (fingerprint/active-turn conflict — meaningful,
 * must surface), 5xx (storage/backend failure — legacy would fail too, and
 * masking it hides a real outage), and network errors (not an `ApiError` at
 * all, since `fetchWithAuthRetry` only wraps HTTP-response failures this
 * way).
 */
export function shouldFallbackToLegacyTransport(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.status === 403 && error.code === "durable_turns_disabled") return true
  if (error.status === 404 && isUnknownApiRouteError(error)) return true
  if (error.status === 405) return true
  return false
}

export function isDurableChatTurnModeEnabled(
  features: Feature[] | undefined,
  mode: DurableChatTurnMode
): boolean {
  const feature = features?.find(({ id }) => id === DURABLE_CHAT_TURNS_FEATURE_ID)
  if (!feature?.enabled) return false

  const enabledModes: unknown = feature.config?.enabled_modes
  if (
    !Array.isArray(enabledModes) ||
    !enabledModes.every(
      (enabledMode) => typeof enabledMode === "string" && durableModes.has(enabledMode)
    )
  ) {
    return false
  }

  return enabledModes.includes(mode)
}
